<template>
  <div v-if="isDataReady && hasUnprocessedMeasurements" class="postprocessing-progress">
    <div class="postprocessing-progress__content">
      <div class="postprocessing-progress__stats">
        <span class="postprocessing-progress__completed">{{ postprocessedMeasurements }}</span>
        <span class="postprocessing-progress__separator">/</span>
        <span class="postprocessing-progress__total">{{ totalMeasurements }}</span>

        <div v-if="isProcessing" class="postprocessing-progress__status">
          <StateProcess title="Applying postprocessing" />
        </div>
      </div>

      <div class="postprocessing-progress__bar-container">
        <div class="postprocessing-progress__bar">
          <div
            class="postprocessing-progress__bar-fill"
            :style="{ width: progressPercentage + '%' }"
          />
        </div>
        <span class="postprocessing-progress__percentage"
          >{{ Math.round(progressPercentage) }}%</span
        >
      </div>
    </div>
  </div>
</template>

<script>
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';
  import StateProcess from '@/uikitProject/states/StateProcess';

  export default {
    name: 'PostprocessingProgressComponent',

    components: {
      StateProcess,
    },

    props: {
      sequenceId: {
        type: Number,
        required: true,
      },
    },

    data() {
      return {
        postprocessedMeasurements: 0,
        totalMeasurements: 0,
        postprocessedSubscription: null,
        isPostprocessedCounterLoaded: false,
        isTotalCounterLoaded: false,
      };
    },

    computed: {
      unprocessedMeasurements() {
        return this.totalMeasurements - this.postprocessedMeasurements;
      },

      hasUnprocessedMeasurements() {
        return this.unprocessedMeasurements > 0;
      },

      progressPercentage() {
        if (this.totalMeasurements === 0) {
          return 0;
        }
        return (this.postprocessedMeasurements / this.totalMeasurements) * 100;
      },

      isProcessing() {
        return this.hasUnprocessedMeasurements && this.totalMeasurements > 0;
      },

      isDataReady() {
        return this.isPostprocessedCounterLoaded && this.isTotalCounterLoaded;
      },
    },

    mounted() {
      this.setupSubscriptions();
    },

    beforeDestroy() {
      this.cleanupSubscriptions();
    },

    methods: {
      async setupSubscriptions() {
        if (!this.sequenceId) {
          return;
        }

        this.postprocessedSubscription = apiMeasurements.subscribeForPostprocessedMeasurement(
          this.sequenceId,
          ({ postprocessedMeasurements }) => {
            this.postprocessedMeasurements = postprocessedMeasurements;
            this.isPostprocessedCounterLoaded = true;
          },
        );

        this.totalMeasurements = await apiMeasurements.getTotalMeasurementsCountBySequenceId(
          this.sequenceId,
        );
        this.isTotalCounterLoaded = true;
      },

      cleanupSubscriptions() {
        if (this.postprocessedSubscription) {
          this.postprocessedSubscription.unsubscribe();
          this.postprocessedSubscription = null;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .postprocessing-progress {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 10px;
    margin-top: 10px;
    background-color: #fafafa;

    &__content {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    &__stats {
      display: flex;
      align-items: center;
      font-size: 14px;
      gap: 6px;
    }

    &__completed {
      font-weight: 600;
      color: $color-text-primary;
    }

    &__separator {
      margin: 0 2px;
      color: $color-text-third;
    }

    &__total {
      color: $color-text-default;
    }

    &__status {
      display: flex;
      align-items: center;
      margin-left: auto;
    }

    &__bar-container {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    &__bar {
      flex: 1;
      height: 6px;
      background-color: #e0e0e0;
      border-radius: 3px;
      overflow: hidden;
    }

    &__bar-fill {
      height: 100%;
      background-color: $color-bg-primary;
      transition: width 0.3s ease;
    }

    &__percentage {
      font-size: 12px;
      color: $color-text-third;
      min-width: 30px;
      text-align: right;
      font-weight: 500;
    }
  }
</style>
